import { useCallback, useState } from 'react'
import syncMutation from 'src/utils/sync-mutation'
import { OvbResolveType, useReservationCreateMutation, useReservedResourceCreateMutation, useSendReservationEmailMutation } from 'src/api/api.generated'
import useErrorSnackBar from 'src/app/hooks/error-snack-bar/error-snack-bar'
import { ReservationNewFormData } from './reservation-new.type'
import { getCoreErrorCodesFromApolloError } from 'src/app/hooks/error-message/apollo-error-message'
import { CoreErrorCode } from 'src/app/hooks/error-message/error-handler.backend-types'
import { ReservationNewGroupFormData } from '../../reservation-group-upsert/form/reservation-group.types'

type UseReservationNewProps = {
  tenantId: string
  reservationId?: string
  planningInfo?: {
    resourceTypeId: string
    resourceId?: string
  }
  next?: () => void
  handleOpenOverbookingModal: (overbookedRows: string[]) => void
  failedReservationAddition?: (ids: string[]) => void
  overbookedReservationAddition: (ids: string[]) => void
}

export default function useReservationNew(props: UseReservationNewProps) {
  const showError = useErrorSnackBar()
  const [createReservation] = useReservationCreateMutation({
    refetchQueries: ['ReservationSearch'],
  })
  const [addReservedResourceToReservation] = useReservedResourceCreateMutation()
  // const [sendReservationEmail] = useSendReservationEmailMutation({
  //   onError: (error) => {
  //     showError(error)
  //     setLoading(false)
  //   },
  // })
  const [loading, setLoading] = useState(false)

  const submitWithOverbooking = useCallback(
    async (data: ReservationNewFormData | ReservationNewGroupFormData, mainGuestName?: string, customerCompanyName?: string) => {
      setLoading(true)
      if (props.reservationId) {
        const failedReservationAddition: string[] = []

        for (let i = 0; i < data.reservedResources.length; i++) {
          const { cartRowId, ...tmpData } = data.reservedResources[i]
          const result = await addReservedResourceToReservation({
            variables: {
              value: {
                tenantId: props.tenantId,
                reservationId: props.reservationId ?? '',
                ...tmpData,
              },
              options: {
                overbooking: OvbResolveType.ALLOW,
              },
            },
            refetchQueries: ['ReservationSearch'],
            awaitRefetchQueries: true,
          })
          if (result.errors) {
            showError(result.errors)
            failedReservationAddition.push(data.reservedResources[i].cartRowId)
          }
        }

        setLoading(false)
        if (props.next && failedReservationAddition.length <= 0) {
          props.next()
        } else if (props.failedReservationAddition) {
          props.failedReservationAddition(failedReservationAddition)
        }
      } else {
        const result = await createReservation({
          variables: {
            value: {
              ...data,
              reservedResources: data.reservedResources.map((rr) => {
                const { cartRowId, ...other } = rr
                return other
              }),
              searchInfo: mainGuestName
                ? {
                    mainGuestName,
                    customerCompanyName,
                    customerPersonName: mainGuestName,
                  }
                : undefined,
            },
            options: {
              overbooking: OvbResolveType.ALLOW,
            },
          },
        })

        if (result.data?.createReservation && !result.errors) {
          setLoading(false)
          if (props.next) props.next()
        } else {
          setLoading(false)
          showError(result.errors)
        }
      }
    },
    [createReservation, showError, props.tenantId, props.next, props.failedReservationAddition],
  )

  const onSubmit = useCallback(
    async (data: ReservationNewFormData | ReservationNewGroupFormData, emailAddress?: string, mainGuestName?: string, customerCompanyName?: string) => {
      setLoading(true)
      if (props.reservationId) {
        const failedReservationAddition: string[] = []
        const overbookedReservationAddition: string[] = []

        for (let i = 0; i < data.reservedResources.length; i++) {
          const { cartRowId, ...tmpData } = data.reservedResources[i]
          const result = await syncMutation(addReservedResourceToReservation, {
            variables: {
              value: {
                tenantId: props.tenantId,
                reservationId: props.reservationId ?? '',
                ...tmpData,
              },
              options: {
                overbooking: OvbResolveType.ERROR,
              },
            },
          })

          if (result.apolloError && getCoreErrorCodesFromApolloError(result.apolloError).includes(CoreErrorCode.OVERBOOKING_NOT_ALLOWED)) {
            overbookedReservationAddition.push(data.reservedResources[i].cartRowId)
          } else if (result.error) {
            showError(result.error)
            failedReservationAddition.push(data.reservedResources[i].cartRowId)
          }
        }

        setLoading(false)
        if (props.next && failedReservationAddition.length <= 0 && overbookedReservationAddition.length <= 0) {
          props.next()
        } else if (props.overbookedReservationAddition && overbookedReservationAddition.length > 0) {
          props.overbookedReservationAddition(overbookedReservationAddition)
        } else if (props.failedReservationAddition && failedReservationAddition.length > 0) {
          props.failedReservationAddition(failedReservationAddition)
        }
      } else {
        let preassociatedResource = props.planningInfo
        const result = await syncMutation(createReservation, {
          variables: {
            value: {
              ...data,
              searchInfo: mainGuestName
                ? {
                    mainGuestName,
                    customerCompanyName,
                    customerPersonName: mainGuestName,
                  }
                : undefined,
              reservedResources: data.reservedResources.map((rr) => {
                const { cartRowId, ...other } = rr
                if (preassociatedResource?.resourceId && preassociatedResource.resourceTypeId === rr.resourceOccupancies[0].resourceTypeId) {
                  const resourceOccupancies = [
                    {
                      ...other.resourceOccupancies[0],
                      resourceId: preassociatedResource.resourceId,
                    },
                  ]
                  const rr = {
                    ...other,
                    resourceOccupancies: resourceOccupancies,
                  }
                  preassociatedResource = undefined
                  return rr
                }
                return other
              }),
            },
            options: {
              overbooking: OvbResolveType.ERROR,
            },
          },
        })

        const createdReservation = result.result?.data?.createReservation

        if (createdReservation && !result.error) {
          setLoading(false)
          if (props.next) props.next()

          return result.result
          // commentato l'invio email dal front end, adesso (30/05/2024) è il back end a mandarla
          // Invio contestuale email
          // if (props.sendEmailOnSave && emailAddress) {
          //   let template: ReservationMessageTemplateCode | undefined = undefined
          //   const rr = createdReservation.reservedResources && createdReservation.reservedResources.length > 0 ? createdReservation.reservedResources[0] : undefined

          //   // 1 recupera la policy più restrittiva
          //   // 2 set email template to use.
          //   if (rr && createdReservation.policy) {
          //     switch (createdReservation.policy.paymentRule.type) {
          //       case 'NOTREQUIRED':
          //         if (createdReservation.policy.paymentRule.guaranteedCreditCard) template = 'LINK2PAY'
          //         else template = 'RESERVATION'
          //         break
          //       case 'FIRSTNIGHT':
          //       case 'PERCENTAGE_TOTAL':
          //       case 'TOTAL':
          //         template = 'LINK2PAY'
          //         break
          //     }
          //   }

          //   // 3 Send email
          //   if (template) {
          //     await sendReservationEmail({
          //       variables: {
          //         input: {
          //           tenantId: data.tenantId,
          //           propertyId: data.propertyId[0],
          //           reservationId: createdReservation.id,
          //           sendTo: emailAddress,
          //           messageTemplateId: template,
          //         },
          //       },
          //     })
          //   }
          // }
        } else if (result.apolloError && getCoreErrorCodesFromApolloError(result.apolloError).includes(CoreErrorCode.OVERBOOKING_NOT_ALLOWED)) {
          setLoading(false)
          props.handleOpenOverbookingModal(data.reservedResources.map((rr) => rr.cartRowId))
        } else {
          setLoading(false)
          showError(result.error)
        }
      }
    },
    [createReservation, showError, props.tenantId, props.next, props.overbookedReservationAddition, props.failedReservationAddition],
  )

  return { onSubmit, submitWithOverbooking, loading }
}
